import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, PagingSidePanelType, SidePanel } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { SidePanelSkeletonLoader } from 'components/SidePanel/SidePanelSkeletonLoader'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useEmbeddedCustomerAgreementsQuery } from 'hooks/queries/useEmbeddedCustomerAgreementsQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { EmbeddedCustomerStatusHighlight } from 'pages/Customers/components/CustomerHighlight'
import { CustomerDetailsHeader } from 'pages/Customers/CustomerDetails/CustomerDetailsHeader'
import { CustomerEntityType } from 'types/customers'
import { highlightRowBasedOnId } from 'utils/table/table'
import {
  EMBEDDED_CUSTOMER_AGREEMENT_STATUSES,
  EMBEDDED_CUSTOMER_STATUSES,
} from '../helpers/constants'
import { EmbeddedCustomer, EmbeddedCustomerData } from '../helpers/types'
import { BusinessCustomer } from './BusinessCustomer'
import { CTASection } from './CTASection'
import { EmbeddedCustomerAgreements } from './EmbeddedCustomerAgreements'
import { IndividualCustomer } from './IndividualCustomer'

export interface EmbeddedCustomerDetailsProps {
  isOpen: boolean
  onClose: () => void
  pageCustomers: PagingSidePanelType
  customerDetails: EmbeddedCustomer
}

export const EmbeddedCustomerDetails = ({
  isOpen,
  onClose,
  pageCustomers,
  customerDetails,
}: EmbeddedCustomerDetailsProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)

  const { name, reference } = customerDetails || {}
  const {
    data: agreementsData = [],
    isFetching: isFetchingAgreements,
    isError: isErrorFetchingAgreements,
    refetch: refetchAgreements,
  } = useEmbeddedCustomerAgreementsQuery(reference)
  const {
    data: customerData,
    isFetching: isFetchingCustomerData,
    isError: isErrorFetchingCustomerData,
    refetch: refetchCustomerData,
  } = useCustomerQuery<EmbeddedCustomerData>(reference)

  const isCompany = customerData?.type === CustomerEntityType.COMPANY
  const isIndividual = customerData?.type === CustomerEntityType.INDIVIDUAL

  const agreementSesstionUrl = customerData?.pendingAgreementSessionUrl
  const showAgreementCTA =
    agreementSesstionUrl && customerData?.status === EMBEDDED_CUSTOMER_STATUSES.PENDING_AGREEMENTS
  const signedAgreements = agreementsData?.filter(
    ({ status }) => status === EMBEDDED_CUSTOMER_AGREEMENT_STATUSES.SIGNED
  )

  const verificationUrl = customerData?.verification?.url
  const isVerificationRequired = customerData?.status === EMBEDDED_CUSTOMER_STATUSES.INFO_REQUIRED
  const showVerificationCTA =
    verificationUrl &&
    (isVerificationRequired || customerData?.status === EMBEDDED_CUSTOMER_STATUSES.PENDING)

  const refetchData = () => {
    isErrorFetchingAgreements && refetchAgreements()
    isErrorFetchingCustomerData && refetchCustomerData()
  }

  const StatusView = useMemo(() => {
    if (isFetchingAgreements || isFetchingCustomerData) return <SidePanelSkeletonLoader />
    if (isErrorFetchingAgreements || isErrorFetchingCustomerData) {
      return (
        <Box paddingX={16} paddingY={32}>
          <RequestErrorStatusCard onReload={refetchData} borderless noPadding />
        </Box>
      )
    }
  }, [
    isFetchingAgreements,
    isFetchingCustomerData,
    isErrorFetchingAgreements,
    isErrorFetchingCustomerData,
  ])

  return (
    <SidePanel
      id="side-panel-embedded-customer"
      isOpen={isOpen}
      onClose={onClose}
      paging={pageCustomers}
      activeRowId={reference}
      onActiveRowChange={highlightRowBasedOnId}
      externalStyle={{ top: `${bannerSpacing}px` }}
      header={
        <Box flexCol gap={16}>
          <CustomerDetailsHeader
            name={name}
            description={customerDetails?.description}
            StatusHighlight={<EmbeddedCustomerStatusHighlight status={customerDetails?.status} />}
          />
          {showAgreementCTA && (
            <CTASection
              ctaLink={agreementSesstionUrl}
              ctaLabel={t('embeddedCustomers.details.agreementsCtaLabel')}
              helpText={t('embeddedCustomers.details.agreementsCtaPending')}
              isPendingStatus
              isCopyAction
            />
          )}
          {showVerificationCTA && (
            <CTASection
              heading={t('embeddedCustomers.details.verificationLink')}
              ctaLink={verificationUrl}
              ctaLabel={t('embeddedCustomers.details.verificationCtaLabel')}
              helpText={t(
                isVerificationRequired
                  ? 'embeddedCustomers.details.verificationCtaPending'
                  : 'embeddedCustomers.details.verificationCtaComplete'
              )}
              isPendingStatus={isVerificationRequired}
            />
          )}
        </Box>
      }
      body={
        <Box width="full">
          <Box flexCol paddingX={4} paddingB={24} gap={24}>
            {StatusView || (
              <>
                {isCompany && (
                  <BusinessCustomer
                    company={customerData?.company}
                    reference={customerData?.reference}
                    riskScore={customerData?.riskScore}
                  />
                )}
                {isIndividual && (
                  <IndividualCustomer
                    individual={customerData?.individual}
                    reference={customerData?.reference}
                  />
                )}
                {!!signedAgreements?.length && (
                  <SidePanelSection heading={t('agreementsSigned')}>
                    <EmbeddedCustomerAgreements agreements={agreementsData} />
                  </SidePanelSection>
                )}
              </>
            )}
          </Box>
        </Box>
      }
    />
  )
}
